import { useTranslation } from 'react-i18next';
import {
  ArrowsRightLeftIcon,
  InformationCircleIcon,
  WindowIcon,
} from '@heroicons/react/24/outline';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_ICON_CLASS_NAME,
  WIZARD_SCREEN_SUBTITLE_CLASS_NAME,
  WIZARD_SCREEN_TITLE_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
  WIZARD_WAITING_TITLE_KEYS,
} from './WhatsAppConnectionWizard.constants';

type tWhatsAppConnectionWizardWaitingProps = {
  /** Whether Meta opened in another tab, which the status then says. */
  isMetaInAnotherTab: boolean;
  /**
   * The path Meta opened on, which the title names; absent for an attempt
   * another surface launched without one.
   */
  path?: tEmbeddedSignupPath;
};

/**
 * The main pane while Meta's window is open. It offers no action: changing
 * path or retrying would leave the open window alive, so both wait until the
 * window closes.
 *
 * @param {tWhatsAppConnectionWizardWaitingProps} props - Where Meta opened
 * and on which path.
 * @returns {JSX.Element} The waiting screen.
 */
export default function WhatsAppConnectionWizardWaiting(
  props: tWhatsAppConnectionWizardWaitingProps,
) {
  const { isMetaInAnotherTab, path } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);

  return (
    <div className="flex flex-col gap-[var(--ls-spacing-6)]">
      <div className="flex flex-col gap-[var(--ls-spacing-2)]">
        <h2 className={WIZARD_SCREEN_TITLE_CLASS_NAME}>
          {t(path ? WIZARD_WAITING_TITLE_KEYS[path] : 'wizard.waiting.title')}
        </h2>
        <p className={WIZARD_SCREEN_SUBTITLE_CLASS_NAME}>
          {t('wizard.waiting.subtitle')}
        </p>
      </div>
      <div className="flex flex-col gap-[var(--ls-spacing-4)]">
        {/* A fixed icon, not a spinner: the next move is the person's, in
            Meta's window. */}
        <div role="status" className="flex gap-[var(--ls-spacing-2)]">
          <WindowIcon className={WIZARD_ICON_CLASS_NAME} aria-hidden="true" />
          <p className={WIZARD_BODY_CLASS_NAME}>
            {t(
              isMetaInAnotherTab
                ? 'wizard.waiting.windowHintFullscreen'
                : 'wizard.waiting.windowHint',
            )}
          </p>
        </div>
        <div className="flex gap-[var(--ls-spacing-2)]">
          <InformationCircleIcon
            className={WIZARD_ICON_CLASS_NAME}
            aria-hidden="true"
          />
          <p className={WIZARD_BODY_CLASS_NAME}>
            {t('wizard.waiting.errorReportHint')}
          </p>
        </div>
        <div className="flex gap-[var(--ls-spacing-2)]">
          <ArrowsRightLeftIcon
            className={WIZARD_ICON_CLASS_NAME}
            aria-hidden="true"
          />
          <p className={WIZARD_BODY_CLASS_NAME}>
            {t('wizard.waiting.changePathHint')}
          </p>
        </div>
      </div>
    </div>
  );
}
